import React from "react";
import { Platform, View } from "react-native";
import { styles } from "./styles";
import { useDispatch, useSelector } from "react-redux";
import { updateAvatar } from "../../../../redux/profile/profileActions";
import { updateAvatarSelector } from "../../../../redux/profile/profileSelector";
import { shadows, useTheme } from "@edgelogistics/edge-ui/lib/providers/theme";
import { useNotification } from "@edgelogistics/edge-ui/lib/providers/notification";
import { Icon, Image, Loading, Menu, Text, TouchableWithEffect } from "@edgelogistics/edge-ui/lib/components";
import images from "@edgelogistics/edge-ui/lib/assets/images";
import buffer from "buffer";
import { MediaTypeOptions, launchImageLibraryAsync } from "expo-image-picker";
import { toArrayBuffer } from "../../../../utils/parseData";
import { UserRolesIdTypes } from "../../../../redux/profile/profileTypes";

export interface UserCardProps {
  user: any;
  handleChangePassword: () => void;
  handleEditUser: () => void;
}

const UserCard: React.FC<UserCardProps> = ({ user, handleChangePassword, handleEditUser }: UserCardProps) => {
  const { loading } = useSelector(updateAvatarSelector);
  const {
    theme: { colors },
  } = useTheme();
  const dispatch = useDispatch();
  const { setNotification } = useNotification();
  const handleNotification = setNotification;

  const menuItems = [
    { text: "Edit", callback: handleEditUser, testID: "profileCardMenuOptionEdit" },
    { text: "Change Password", callback: handleChangePassword, testID: "profileCardMenuOptionChangePassword" },
  ];

  const onSuccess = () => {
    handleNotification({ message: "Avatar was updated successfully", type: "success" });
  };
  const onFailure = (error: any) => {
    handleNotification({ message: error, type: "error" });
  };

  const getWebAvatarFile = (uri: string) => {
    const uriSplit = uri.split("base64,");
    const base64 = uriSplit.pop() || "";
    const metadata = uriSplit.pop() || "";

    if (!metadata.includes("jpg") && !metadata.includes("jpeg") && !metadata.includes("png")) {
      handleNotification({ message: "The image must be jpg, jpeg or png.", type: "error" });

      return null;
    }

    const base64Length = uri.length - (uri.indexOf(",") + 1);
    const padding = uri.charAt(uri.length - 2) === "=" ? 2 : uri.endsWith("=") ? 1 : 0;
    const fileSize = base64Length * 0.75 - padding;

    if (fileSize * 0.000001 > 2) {
      handleNotification({ message: "The image size must be less than 2MB.", type: "error" });

      return null;
    }

    const buf = buffer.Buffer.from(base64, "base64");
    const arrayBuffer = toArrayBuffer(buf);
    const file = new File([ arrayBuffer ], `avatar-${Date.now()}.png`, {
      type: "image/png",
    });

    return file;
  };

  const getMobileAvatarFile = (uri: string) => {
    const name = uri.split("/").pop() || "";
    const match = /\.(\w+)$/.exec(name);
    const type = match ? `image/${match[1]}` : "image";

    return { uri, name, type };
  };

  const handleImagePicker = async () => {
    try {
      const result = await launchImageLibraryAsync({
        mediaTypes    : MediaTypeOptions.Images,
        allowsEditing : true,
        quality       : 0.5,
      });

      if (!result.cancelled) {
        const { uri } = result;
        const payload = new FormData();

        const file = Platform.OS === "web" ? getWebAvatarFile(uri) : getMobileAvatarFile(uri);
        // @ts-expect-error

        payload.append("files", file);
        if (payload) {
          dispatch(updateAvatar(payload, onSuccess, onFailure));
        }
      }
    } catch (error: any) {
      handleNotification({ message: error, type: "error" });
    }
  };

  return (
    <View style={[ { backgroundColor: colors.BG_CARD }, styles.container, shadows().first_shadow ]}>
      <View style={styles.header}>
        <View style={styles.timezone}>
          <Icon name="Clock" width={15} height={15} color={colors.GRAY} />
          <Text
            text={
              user?.timezone ?
                `${user?.timezone?.abbreviation}, ${user?.timezone?.offset >= 0 ? "+" : ""}${user?.timezone?.offset}` :
                "- -"
            }
            style={{ marginLeft: 5, fontSize: 12, fontWeight: "700" }}
            color="GRAY"
          />
        </View>
        <Menu items={menuItems}>
          <View style={styles.options}>
            <Icon name="ShowMore" width={10} height={14} color={colors.GRAY} />
          </View>
        </Menu>
      </View>
      <View style={styles.photo}>
        {loading ? (
          <View style={[ shadows().first_shadow, styles.avatar ]}>
            <Loading />
          </View>
        ) : (
          <Image
            source={user?.avatar ? `${user.avatar}?${Date.now()}` : images.defaultAvatar}
            style={{ width: 100, height: 100, borderRadius: 9999 }}
          />
        )}
        <View style={[ styles.changePhotoButton, shadows().first_shadow ]}>
          <TouchableWithEffect onPress={handleImagePicker}>
            <Icon name="Camera" width={15} height={15} color={colors.GRAY} />
          </TouchableWithEffect>
        </View>
      </View>
      <View>
        <View style={[ styles.roleButton, { backgroundColor: colors.GREEN } ]}>
          <Text text={user?.role?.description || "- -"} style={{ fontSize: 12 }} color={colors.BLACK_WHITE} />
        </View>
      </View>
      <View style={styles.details}>
        <UserDetail title="Name" description={user?.name || "- -"} />
        <UserDetail title="Email" description={user?.email || "- -"} />
        {user.role_id !== UserRolesIdTypes.admin && (
          <UserDetail title="Dispatch Division" description={user?.dispatch_division?.country || "- -"} />
        )}
      </View>
    </View>
  );
};

interface UserDetailProps {
  title: string;
  description: string;
}

const UserDetail: React.FC<UserDetailProps> = ({ title, description }: UserDetailProps) => {
  const {
    theme: { colors },
  } = useTheme();

  return (
    <View style={styles.detail}>
      <Text text={title} style={{ marginLeft: 5, fontSize: 12 }} color={colors.GRAY} />
      <Text text={description} style={{ marginLeft: 5, fontSize: 14 }} color={colors.PRIMARY} />
    </View>
  );
};

export default UserCard;
